import React, { useState } from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Grid,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import WarehouseInfo from "../../Forms/Warehouses/WarehouseInfo";
import WarehouseReceipt from "../../Forms/Warehouses/WarehouseReceipt";
import WarehouseMapping from "../../Forms/Warehouses/WarehouseMapping";

const Warehouses = (props) => {
  const {
    handleChange,
    handleMappingChange,
    handleHeadersChange,
    steps,
    toggleConfirmModal,
    warehouse,
    warehouseFields,
    warehouseHeaders,
  } = props;
  const [activeStep, setActiveStep] = useState(0);
  const [skipped, setSkipped] = useState(new Set());

  const theme = useTheme();
  const xsWidth = useMediaQuery(theme.breakpoints.only("xs"));
  const smWidth = useMediaQuery(theme.breakpoints.only("sm"));

  const isStepOptional = (step) => {
    const optionalStep = !isNaN(warehouse.id) ? 1 : null;
    return step === optionalStep;
  };

  const isStepSkipped = (step) => {
    return skipped.has(step);
  };

  const handleNext = () => {
    let newSkipped = skipped;
    if (activeStep !== steps.length - 1) {
      if (isStepSkipped(activeStep)) {
        newSkipped = new Set(newSkipped.values());
        newSkipped.delete(activeStep);
      }

      setActiveStep(activeStep + 1);
      setSkipped(newSkipped);
    } else {
      toggleConfirmModal();
    }
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const handleSkip = () => {
    if (!isStepOptional(activeStep)) {
      throw new Error("You can't skip a step that isn't optional.");
    }

    setActiveStep(activeStep + 1);
    setSkipped((prevSkipped) => {
      const newSkipped = new Set(prevSkipped.values());
      newSkipped.add(activeStep);
      return newSkipped;
    });
  };

  const handleCancel = () => {
    props.history.push(`/setup`);
  };

  return (
    <Box sx={{ maxWidth: "100%" }}>
      <Stepper
        activeStep={activeStep}
        className="rootStepper"
        orientation={xsWidth || smWidth ? "vertical" : "horizontal"}
      >
        {steps.map((label, index) => {
          return (
            <Step
              key={index}
              className={activeStep === index ? "activeStep" : "steps"}
            >
              <StepLabel>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      {activeStep === steps.length ? (
        <>
          <Typography sx={{ mt: 2, mb: 1 }}>
            All steps completed - you have finished
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
            <Box sx={{ flex: "1 1 auto" }} />
          </Box>
        </>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mt: 2, mb: 1 }}>
            {
              {
                0: (
                  <>
                    <WarehouseInfo
                      handleChange={handleChange}
                      warehouse={warehouse}
                    ></WarehouseInfo>
                  </>
                ),
                1: (
                  <>
                    <WarehouseReceipt
                      handleHeadersChange={handleHeadersChange}
                    ></WarehouseReceipt>
                  </>
                ),
                2: (
                  <>
                    <WarehouseMapping
                      handleMappingChange={handleMappingChange}
                      warehouse={warehouse}
                      warehouseHeaders={warehouseHeaders}
                      warehouseFields={warehouseFields}
                    ></WarehouseMapping>
                  </>
                ),
              }[activeStep]
            }
          </Grid>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              pt: 2,
              justifyContent: "flex-end",
            }}
          >
            <Button variant="contained" onClick={handleCancel} sx={{ mr: 1 }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="inherit"
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ mr: 1 }}
            >
              Back
            </Button>
            {isStepOptional(activeStep) && (
              <Button
                color="inherit"
                onClick={handleSkip}
                sx={{ mr: 1 }}
              >
                Skip
              </Button>
            )}
            <Button variant="contained" onClick={handleNext}>
              {activeStep === steps.length - 1 ? "Finish" : "Next"}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default Warehouses;
